import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';

interface LoginFormData {
  email: string;
  password: string;
}

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    constituencies: string[];
  };
}

interface AccessStatus {
  enabled: boolean;
  teamDomain: string;
  requireMFA: boolean;
  mfaRequiredRoles: string[];
  config: {
    jwksUrl: string;
    issuer: string;
  };
}

interface SessionData {
  authenticated: boolean;
  user?: any;
  role?: any;
  constituencies?: any[];
  authMethod: 'jwt' | 'cloudflare-access';
  cloudflareAccess?: {
    enabled: boolean;
    mfaVerified: boolean;
    mfaMethods: string[];
    accessData?: any;
  };
  authPreferences?: any;
}

export default function LoginForm() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authMethod, setAuthMethod] = useState<'jwt' | 'cloudflare-access'>('jwt');
  const { toast } = useToast();

  // Check if user is already authenticated via session check
  const { data: sessionData, isLoading: sessionLoading } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const response = await fetch('/api/auth/session', {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Session check failed');
      }
      return response.json() as Promise<SessionData>;
    },
    retry: false,
  });

  // Check Cloudflare Access configuration
  const { data: accessStatus } = useQuery({
    queryKey: ['access-status'],
    queryFn: async () => {
      const response = await fetch('/api/auth/access-status');
      if (!response.ok) {
        throw new Error('Failed to get Access status');
      }
      const data = await response.json();
      return data.data as AccessStatus;
    },
    retry: false,
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (sessionData?.authenticated) {
      window.location.href = '/dashboard';
    }
  }, [sessionData]);

  // Determine preferred authentication method
  useEffect(() => {
    if (accessStatus?.enabled && sessionData?.authMethod) {
      setAuthMethod(sessionData.authMethod);
    } else if (accessStatus?.enabled) {
      setAuthMethod('cloudflare-access');
    } else {
      setAuthMethod('jwt');
    }
  }, [accessStatus, sessionData]);

  const loginMutation = useMutation({
    mutationFn: async (data: LoginFormData) => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await fetch('/api/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || t('auth.loginFailed'));
        }

        const authData: AuthResponse = await response.json();

        // Store tokens in localStorage
        localStorage.setItem('accessToken', authData.accessToken);
        localStorage.setItem('refreshToken', authData.refreshToken);
        localStorage.setItem('user', JSON.stringify(authData.user));

        toast({
          title: t('auth.loginSuccess'),
          description: t('auth.welcomeBack', { name: authData.user.name }),
        });

        return authData;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : t('common.error');
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    onSuccess: (data) => {
      // Redirect to dashboard after successful login
      window.location.href = '/dashboard';
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: t('auth.loginFailed'),
        description: error.message,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loginMutation.mutate(formData);
  };

  if (sessionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <Card>
          <CardHeader>
            <CardTitle className={`text-2xl font-bold text-center ${t('common:language') === 'ta' ? 'tamil-heading' : ''}`}>
              {t('auth.loginTitle')}
            </CardTitle>
            <CardDescription className={t('language') === 'ta' ? 'tamil-text' : ''}>
              {t('auth.loginDescription')}
            </CardDescription>

            {/* Authentication Status Indicator */}
            {accessStatus?.enabled && (
              <div className="flex items-center justify-center space-x-2 mt-2">
                <Badge variant="secondary" className="bg-green-100 text-green-800">
                  Cloudflare Access Enabled
                </Badge>
                {accessStatus.requireMFA && (
                  <Badge variant="outline" className="border-blue-500 text-blue-600">
                    MFA Required
                  </Badge>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Authentication Method Selector */}
            {accessStatus?.enabled && (
              <Tabs value={authMethod} onValueChange={(value) => setAuthMethod(value as 'jwt' | 'cloudflare-access')} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="cloudflare-access" className="flex items-center space-x-2">
                    <span>Access</span>
                    <Badge variant="secondary" className="text-xs">Recommended</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="jwt">Password</TabsTrigger>
                </TabsList>

                <TabsContent value="cloudflare-access" className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-blue-800">Cloudflare Access Authentication</h3>
                        <div className="mt-1 text-sm text-blue-700">
                          <p>You'll be redirected to authenticate via Cloudflare Access.</p>
                          {accessStatus.requireMFA && (
                            <p className="mt-1">Multi-factor authentication is required.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={() => {
                      // Redirect to Cloudflare Access login
                      window.location.href = `https://${accessStatus.teamDomain}.cloudflareaccess.com`;
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={isLoading}
                  >
                    {isLoading ? t('common.loading') : 'Continue with Cloudflare Access'}
                  </Button>
                </TabsContent>

                <TabsContent value="jwt" className="space-y-4">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <div>
                        <Label htmlFor="email" className={t('language') === 'ta' ? 'tamil-ui' : ''}>
                          {t('auth.email')}
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder={t('auth.email')}
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className={`w-full ${t('language') === 'ta' ? 'tamil-ui' : ''}`}
                        />
                      </div>

                      <div>
                        <Label htmlFor="password" className={t('language') === 'ta' ? 'tamil-ui' : ''}>
                          {t('auth.password')}
                        </Label>
                        <Input
                          id="password"
                          type="password"
                          placeholder={t('auth.password')}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required
                          className={`w-full ${t('language') === 'ta' ? 'tamil-ui' : ''}`}
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isLoading}
                    >
                      {isLoading ? t('common.loading') : t('auth.login')}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            )}

            {/* Fallback form when Access is not enabled */}
            {!accessStatus?.enabled && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <div>
                    <Label htmlFor="email" className={t('language') === 'ta' ? 'tamil-ui' : ''}>
                      {t('auth.email')}
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('auth.email')}
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className={`w-full ${t('language') === 'ta' ? 'tamil-ui' : ''}`}
                    />
                  </div>

                  <div>
                    <Label htmlFor="password" className={t('language') === 'ta' ? 'tamil-ui' : ''}>
                      {t('auth.password')}
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder={t('auth.password')}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className={`w-full ${t('language') === 'ta' ? 'tamil-ui' : ''}`}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? t('common.loading') : t('auth.login')}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Authentication Information */}
        <div className="text-center text-sm text-gray-500">
          {accessStatus?.enabled ? (
            <div className="space-y-1">
              <p>Protected by Cloudflare Access</p>
              <p className="text-xs">
                {authMethod === 'cloudflare-access'
                  ? 'Click above to authenticate securely with MFA'
                  : 'Or use your traditional password credentials'}
              </p>
            </div>
          ) : (
            <p>Traditional authentication enabled</p>
          )}
        </div>
      </div>
    </div>
  );
}
